import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonModal,
  IonSpinner,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import {
  cameraOutline,
  checkmarkCircleOutline,
  closeOutline,
  imagesOutline,
  trashOutline,
} from 'ionicons/icons';
import { useEffect, useRef, useState } from 'react';
import {
  getCaptureProvider,
  type CapturedPhoto,
} from '../lib/adapters/camera/index.js';
import { beginOperatorExternalActivity } from '../lib/auth/operatorExternalActivity.js';
import { toUserMessage } from '../lib/errors.js';
import { compressImageIfNeeded } from '../lib/imageCompress.js';
import { confirmDialog } from '../lib/ui/dialogs.js';
import './ProductionLotCaptureModal.css';

export const MAX_PRODUCTION_BATCH_PHOTOS = 20;

export interface CapturedBatchPhoto {
  id: string;
  photo: CapturedPhoto;
  capturedAt: string;
  previewDataUrl: string;
}

interface ProductionLotCaptureModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  /** Persiste localmente l'intero batch. Non avvia OCR o match. */
  onSave: (photos: CapturedBatchPhoto[]) => Promise<void>;
}

/**
 * Sessione fotografica per una produzione.
 *
 * Ogni scatto usa lo stesso provider della cattura DDT e della fotografia
 * lotto. La camera nativa resta quindi fuori dal DOM dell'app: nessuno stream
 * `getUserMedia`, video o canvas continua a consumare CPU/GPU mentre
 * l'operatore inquadra. Al rientro conserviamo il JPEG compresso per l'OCR e
 * una miniatura leggera per mostrare l'intera sessione senza saturare la GPU.
 */
export function ProductionLotCaptureModal({
  isOpen,
  onDismiss,
  onSave,
}: ProductionLotCaptureModalProps): JSX.Element {
  const authorizedDismissRef = useRef(false);
  const [photos, setPhotos] = useState<CapturedBatchPhoto[]>([]);
  const [capturing, setCapturing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setPhotos([]);
      return undefined;
    }

    const endExternalActivity = beginOperatorExternalActivity();
    setPhotos([]);
    setCapturing(false);
    setBusy(false);
    setError(null);
    authorizedDismissRef.current = false;
    return endExternalActivity;
  }, [isOpen]);

  async function preparePhoto(photo: CapturedPhoto): Promise<CapturedBatchPhoto> {
    const compressed = await compressImageIfNeeded(photo.base64, photo.mime);
    const preview = await compressImageIfNeeded(compressed.b64, compressed.mime, {
      maxDim: 320,
      quality: 0.55,
    });
    return {
      id: crypto.randomUUID(),
      capturedAt: new Date().toISOString(),
      photo: { base64: compressed.b64, mime: compressed.mime },
      previewDataUrl: `data:${preview.mime};base64,${preview.b64}`,
    };
  }

  async function captureWithSystemCamera(): Promise<void> {
    if (busy || capturing || photos.length >= MAX_PRODUCTION_BATCH_PHOTOS) return;
    setCapturing(true);
    setError(null);
    try {
      const photo = await getCaptureProvider().takePhoto('camera');
      if (!photo) return;
      const next = await preparePhoto(photo);
      setPhotos((current) => (
        current.length >= MAX_PRODUCTION_BATCH_PHOTOS ? current : [...current, next]
      ));
    } catch (captureError) {
      setError(toUserMessage(captureError, 'Cattura non riuscita'));
    } finally {
      setCapturing(false);
    }
  }

  async function addFromGallery(): Promise<void> {
    if (busy || capturing || photos.length >= MAX_PRODUCTION_BATCH_PHOTOS) return;
    setCapturing(true);
    setError(null);
    try {
      const remaining = MAX_PRODUCTION_BATCH_PHOTOS - photos.length;
      const selected = await getCaptureProvider().pickPhotos('gallery', remaining);
      const prepared: CapturedBatchPhoto[] = [];
      // Una alla volta: evitiamo di decodificare insieme molte foto grandi.
      for (const photo of selected.slice(0, remaining)) {
        prepared.push(await preparePhoto(photo));
      }
      setPhotos((current) => [
        ...current,
        ...prepared.slice(0, MAX_PRODUCTION_BATCH_PHOTOS - current.length),
      ]);
    } catch (captureError) {
      setError(toUserMessage(captureError, 'Importazione foto non riuscita'));
    } finally {
      setCapturing(false);
    }
  }

  async function saveBatch(): Promise<void> {
    if (busy || capturing || photos.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      await onSave(photos);
      authorizedDismissRef.current = true;
      setPhotos([]);
      onDismiss();
    } catch (saveError) {
      setError(toUserMessage(saveError, 'Salvataggio foto non riuscito'));
    } finally {
      setBusy(false);
    }
  }

  async function discardAndDismiss(): Promise<void> {
    if (busy || capturing) return;
    if (
      photos.length > 0
      && !(await confirmDialog('Scartare tutte le foto appena acquisite?', {
        header: 'Uscire dalla fotocamera?',
        okText: 'Scarta foto',
        danger: true,
      }))
    ) return;
    authorizedDismissRef.current = true;
    setPhotos([]);
    onDismiss();
  }

  const full = photos.length >= MAX_PRODUCTION_BATCH_PHOTOS;
  const statusText = photos.length === 0
    ? 'Nessuna foto acquisita'
    : `${photos.length} ${photos.length === 1 ? 'foto acquisita' : 'foto acquisite'}`;
  const remainingText = full
    ? 'Limite raggiunto'
    : `${MAX_PRODUCTION_BATCH_PHOTOS - photos.length} ancora disponibili`;

  return (
    <IonModal
      data-testid="production-lot-capture"
      isOpen={isOpen}
      onDidPresent={() => { void captureWithSystemCamera(); }}
      onDidDismiss={() => {
        setPhotos([]);
        authorizedDismissRef.current = false;
        onDismiss();
      }}
      canDismiss={async () => {
        if (authorizedDismissRef.current) return true;
        if (busy || capturing) return false;
        return photos.length === 0 || await confirmDialog(
          'Scartare tutte le foto appena acquisite?',
          {
            header: 'Uscire dalla fotocamera?',
            okText: 'Scarta foto',
            danger: true,
          },
        );
      }}
    >
      <IonHeader className="te-photo-capture__header">
        <IonToolbar className="te-photo-capture__toolbar">
          <IonTitle>Fotografa le confezioni</IonTitle>
          <IonButtons slot="end">
            <IonButton
              disabled={busy || capturing}
              onClick={() => { void discardAndDismiss(); }}
              aria-label="Chiudi e scarta la sessione fotografica"
              className="te-photo-capture__close"
            >
              <IonIcon icon={closeOutline} slot="icon-only" />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen className="te-photo-capture__content">
        <div className="te-photo-capture">
          <section className="te-photo-capture__intro" aria-labelledby="photo-capture-guide-title">
            <div className="te-photo-capture__intro-icon" aria-hidden="true">
              <IonIcon icon={cameraOutline} />
            </div>
            <div className="te-photo-capture__intro-copy">
              <span className="te-photo-capture__eyebrow">Sessione foto lotti</span>
              <h2 id="photo-capture-guide-title">Una confezione alla volta</h2>
              <p>
                Inquadra bene lotto e scadenza. Dopo ogni scatto torni qui e
                puoi passare subito alla confezione successiva.
              </p>
            </div>
          </section>

          <main className="te-photo-capture__workspace">
            <div className="te-photo-capture__session-status">
              <div>
                <span className="te-photo-capture__session-label">La tua sessione</span>
                <strong role="status" aria-live="polite">{statusText}</strong>
              </div>
              <span className={full ? 'te-photo-capture__count is-full' : 'te-photo-capture__count'}>
                {photos.length}/{MAX_PRODUCTION_BATCH_PHOTOS}
              </span>
            </div>
            <div
              className="te-photo-capture__progress"
              role="progressbar"
              aria-label="Foto acquisite nella sessione"
              aria-valuemin={0}
              aria-valuemax={MAX_PRODUCTION_BATCH_PHOTOS}
              aria-valuenow={photos.length}
            >
              <span style={{ width: `${(photos.length / MAX_PRODUCTION_BATCH_PHOTOS) * 100}%` }} />
            </div>

            {photos.length > 0 ? (
              <div
                role="list"
                aria-label="Foto acquisite"
                className="te-photo-capture__grid"
              >
                {photos.map((item, index) => (
                  <div
                    key={item.id}
                    role="listitem"
                    className="te-photo-capture__thumbnail"
                  >
                    <img
                      src={item.previewDataUrl}
                      alt={`Foto confezione ${index + 1}`}
                      decoding="async"
                    />
                    <span className="te-photo-capture__photo-number">
                      {index + 1}
                    </span>
                    <button
                      type="button"
                      disabled={busy || capturing}
                      onClick={() => setPhotos((current) => (
                        current.filter((photo) => photo.id !== item.id)
                      ))}
                      aria-label={`Rimuovi foto ${index + 1}`}
                      className="te-photo-capture__remove"
                    >
                      <IonIcon icon={trashOutline} aria-hidden="true" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="te-photo-capture__empty">
                <div className="te-photo-capture__empty-icon" aria-hidden="true">
                  <IonIcon icon={cameraOutline} />
                </div>
                <strong>Pronto per la prima confezione</strong>
                <p>Appoggiala su un piano stabile e fai entrare l’etichetta nell’inquadratura.</p>
              </div>
            )}

            <p className="te-photo-capture__remaining">{remainingText}</p>

            {error && (
              <div role="alert" className="te-photo-capture__error">
                {error}
              </div>
            )}
          </main>

          <footer className="te-photo-capture__actions">
            <div className="te-photo-capture__capture-actions">
            <IonButton
              expand="block"
              fill={photos.length === 0 ? 'solid' : 'outline'}
              disabled={busy || capturing || full}
              onClick={() => { void captureWithSystemCamera(); }}
              aria-label="Apri la fotocamera di sistema per un nuovo scatto"
              className="te-photo-capture__action-button"
            >
              {capturing ? <IonSpinner name="dots" /> : <IonIcon icon={cameraOutline} slot="start" />}
              {capturing ? 'Acquisizione…' : full ? 'Limite raggiunto' : photos.length > 0 ? 'Altra foto' : 'Scatta foto'}
            </IonButton>
            <IonButton
              expand="block"
              fill="outline"
              disabled={busy || capturing || full}
              onClick={() => { void addFromGallery(); }}
              aria-label="Seleziona più foto dalla galleria"
              className="te-photo-capture__action-button"
            >
              <IonIcon icon={imagesOutline} slot="start" />
              Più foto
            </IonButton>
            </div>
            <IonButton
              expand="block"
              disabled={busy || capturing || photos.length === 0}
              onClick={() => { void saveBatch(); }}
              className="te-photo-capture__save"
            >
              {busy ? <IonSpinner name="dots" /> : <IonIcon icon={checkmarkCircleOutline} slot="start" />}
              {busy ? 'Salvataggio…' : `Usa ${photos.length} foto`}
            </IonButton>
          </footer>
        </div>
      </IonContent>
    </IonModal>
  );
}
